import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { tap, map } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Workbook, WorkbookPayload } from '../models/reinsurance.model';
import { GljeRow } from '../components/glje-tab/glje-tab';
import { StatementRow } from '../components/statement-tab/statement-tab';
import { CashSettlement } from '../components/cash-settlement-tab/cash-settlement-tab';

export interface StatementResponse {
  rows?: StatementRow[];
  is_posted?: boolean;
  itd_year?: string | null;
  current_year?: string | null;
}

/** Filters and optional ratio overrides sent with a Claims + Bordereau monthly upload. The ASLOB is no longer picked here — the backend derives it from the treaty's own Classes of Business & Coverages. Ratios are percent units (47 = 47%); left undefined they default to the ITD baseline's ratios. */
export interface StatementMonthlyOptions {
  program: string;
  coverholder: string;
  /** YYYY-MM; detected from the file names when omitted. */
  monthKey?: string;
  directLossPct?: number;
  directLAEDCCPct?: number;
  directLAEAOEPct?: number;
  directLossALAEPct?: number;
}

export interface JournalEntryBatch {
  id: number;
  batch_number: string;
}

@Injectable({
  providedIn: 'root',
})
export class ReinsuranceApi {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = environment.apiUrl;

  readonly workbooksChanged$ = new Subject<void>();

  /**
   * All workbooks, always fresh: treaties, MGAs and uploads change them from several screens, so
   * a cached copy showed deleted treaties' ITDs and missed new ones.
   */
  getWorkbooks(): Observable<Workbook[]> {
    return this.http.get<Workbook[]>(`${this.apiUrl}/workbooks`);
  }

  getPrograms(): Observable<{ name: string }[]> {
    return this.http.get<{ name: string }[]>(`${this.apiUrl}/workbooks/programs`);
  }

  getWorkbook(id: number | string): Observable<Workbook> {
    return this.http.get<Workbook>(`${this.apiUrl}/workbooks/${id}`);
  }

  deleteWorkbook(id: number | string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/workbooks/${id}`).pipe(
      tap(() => {
        this.workbooksChanged$.next();
      }),
    );
  }

  /** A treaty with no ITD: saves a zero ITD for the month before `firstMonthKey` (YYYY-MM). */
  startFresh(program: string, firstMonthKey: string): Observable<Workbook> {
    return this.http
      .post<Workbook>(`${this.apiUrl}/statement-monthly/start-fresh`, { program, firstMonthKey })
      .pipe(
        tap(() => {
          this.workbooksChanged$.next();
        }),
      );
  }

  uploadWorkbook(
    file: File,
    overwrite: boolean = false,
    program?: string,
    losses?: { lossPick?: number; laeDcc?: number; laeAoe?: number },
  ): Observable<{ workbook: Workbook }> {
    const formData = new FormData();
    formData.append('file', file);
    if (overwrite) {
      formData.append('overwrite', 'true');
    }
    if (program) {
      formData.append('program', program);
    }
    if (losses?.lossPick !== undefined && losses.lossPick !== null) {
      formData.append('lossPick', String(losses.lossPick));
    }
    if (losses?.laeDcc !== undefined && losses.laeDcc !== null) {
      formData.append('laeDcc', String(losses.laeDcc));
    }
    if (losses?.laeAoe !== undefined && losses.laeAoe !== null) {
      formData.append('laeAoe', String(losses.laeAoe));
    }
    return this.http.post<{ workbook: Workbook }>(`${this.apiUrl}/workbooks/upload`, formData).pipe(
      tap(() => {
        this.workbooksChanged$.next();
      }),
    );
  }

  /** Programs whose ITD baseline came from an ITD financial statement, so their monthly upload needs a Claims + Bordereau pair. */
  getStatementMonthlyPrograms(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/statement-monthly/programs`);
  }

  /** Reads the reporting month (YYYY-MM, or '') out of one Claims or Bordereau file. A Bordereau file is read using the treaty's own configured ASL code(s), found server-side from `program`. */
  inspectStatementFile(
    file: File,
    kind: 'claims' | 'bordereau',
    program: string,
  ): Observable<{ monthKey: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('kind', kind);
    formData.append('program', program);
    return this.http.post<{ monthKey: string }>(
      `${this.apiUrl}/statement-monthly/inspect`,
      formData,
    );
  }

  /** `extra`: further raw SSPI exports (WRITTEN / Claims); the server sorts every file by its contents. */
  uploadStatementMonthly(
    claims: File | null,
    bordereau: File | null,
    options: StatementMonthlyOptions,
    extra: File[] = [],
  ): Observable<{ message: string; workbook: Workbook }> {
    const formData = new FormData();
    if (claims) formData.append('claims', claims);
    if (bordereau) formData.append('bordereau', bordereau);
    for (const file of extra) formData.append('files', file);
    for (const [key, value] of Object.entries(options)) {
      if (value !== undefined && value !== null && value !== '') {
        formData.append(key, String(value));
      }
    }
    return this.http
      .post<{
        message: string;
        workbook: Workbook;
      }>(`${this.apiUrl}/statement-monthly/upload`, formData)
      .pipe(
        tap(() => {
          this.workbooksChanged$.next();
        }),
      );
  }

  updateExhibit(
    workbookId: number | string,
    stateCode: string,
    data: WorkbookPayload,
  ): Observable<Workbook> {
    return this.http.put<Workbook>(
      `${this.apiUrl}/workbooks/${workbookId}/exhibits/${stateCode}`,
      data,
    );
  }

  updateRates(workbookId: number | string, data: WorkbookPayload): Observable<Workbook> {
    return this.http.put<Workbook>(`${this.apiUrl}/workbooks/${workbookId}/rates`, data);
  }

  updateCashSettlement(workbookId: number | string, data: WorkbookPayload): Observable<Workbook> {
    return this.http.put<Workbook>(`${this.apiUrl}/workbooks/${workbookId}/cash-settlement`, data);
  }

  getReinsuranceStatement(
    id: number | string,
    stateCode: string,
  ): Observable<StatementRow[] | StatementResponse> {
    return this.http.get<StatementRow[] | StatementResponse>(
      `${this.apiUrl}/workbooks/${id}/reinsurance-statement/${stateCode}`,
    );
  }

  getIsPosted(id: number | string, stateCode: string): Observable<{ is_posted: boolean }> {
    return this.http.get<{ is_posted: boolean }>(
      `${this.apiUrl}/workbooks/${id}/reinsurance-statement/${stateCode}/is-posted`,
    );
  }

  /** Every state's GL lines for the month (TOTAL view) — the lines "Export All States" writes. */
  getAllStatesGLJournalEntries(id: number | string): Observable<GljeRow[]> {
    return this.http.get<GljeRow[]>(`${this.apiUrl}/workbooks/${id}/gl-journal-entries-all-states`);
  }

  getGLJournalEntries(id: number | string, stateCode: string): Observable<GljeRow[]> {
    return this.http.get<GljeRow[]>(
      `${this.apiUrl}/workbooks/${id}/gl-journal-entries/${stateCode}`,
    );
  }

  postToJournalEntries(
    id: number | string,
    stateCode: string,
    customRows?: GljeRow[],
    postingDate?: string,
  ): Observable<JournalEntryBatch> {
    return this.http.post<JournalEntryBatch>(
      `${this.apiUrl}/workbooks/${id}/post-to-journal-entries/${stateCode}`,
      { customRows, postingDate },
    );
  }

  exportGLJournalEntries(
    id: number | string,
    stateCode: string,
    customRows: GljeRow[],
  ): Observable<{ blob: Blob; filename?: string }> {
    return this.http
      .post(
        `${this.apiUrl}/workbooks/${id}/export-gl-journal-entries/${stateCode}`,
        { customRows },
        { responseType: 'blob', observe: 'response' },
      )
      .pipe(
        map(res => {
          const cd =
            res.headers.get('content-disposition') ?? res.headers.get('Content-Disposition') ?? '';
          let filename: string | undefined;
          const match = /filename=["']?([^"';]+)["']?/i.exec(cd);
          if (match?.[1]) {
            filename = match[1].trim();
          }
          return { blob: res.body ?? new Blob(), filename };
        }),
      );
  }

  exportGLJournalEntriesExcel(
    id: number | string,
    stateCode: string,
    customRows: GljeRow[],
  ): Observable<{ blob: Blob; filename?: string }> {
    return this.http
      .post(
        `${this.apiUrl}/workbooks/${id}/export-gl-journal-entries-excel/${stateCode}`,
        { customRows },
        { responseType: 'blob', observe: 'response' },
      )
      .pipe(
        map(res => {
          const cd =
            res.headers.get('content-disposition') ?? res.headers.get('Content-Disposition') ?? '';
          let filename: string | undefined;
          const match = /filename=["']?([^"';]+)["']?/i.exec(cd);
          if (match?.[1]) {
            filename = match[1].trim();
          }
          return { blob: res.body ?? new Blob(), filename };
        }),
      );
  }

  getCashSettlementCalculations(
    id: number | string,
    stateCode: string = 'TOTAL',
  ): Observable<CashSettlement> {
    return this.http.get<CashSettlement>(
      `${this.apiUrl}/workbooks/${id}/cash-settlement-calculations?stateCode=${stateCode}`,
    );
  }

  updateMappings(id: number | string, data: WorkbookPayload): Observable<WorkbookPayload> {
    return this.http.put<WorkbookPayload>(`${this.apiUrl}/workbooks/${id}/mappings`, data);
  }

  createManualITD(data: WorkbookPayload): Observable<WorkbookPayload> {
    return this.http.post<WorkbookPayload>(`${this.apiUrl}/workbooks/manual-itd`, data).pipe(
      tap(() => {
        this.workbooksChanged$.next();
      }),
    );
  }

  // Must go through HttpClient (not window.open/a plain navigation) so the
  // auth interceptor attaches the Bearer token to the request.
  exportTreatyWorkbook(program: string): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/workbooks/export/${encodeURIComponent(program)}`, {
      responseType: 'blob',
    });
  }
}
